import {
  EXCEPTION_DISPOSITION,
  type ExceptionOutcome,
  type ExceptionPolicy,
} from 'clients/exception-handling/interface'
import { logger, type LoggerClient } from 'clients/logger'
import { isCancellation } from 'clients/logger/is-cancellation'

/** Record a decision made by the catch owner; never report and then rethrow. */
export const handleException = (
  cause: unknown,
  policy: ExceptionPolicy,
  reporter: LoggerClient = logger,
): ExceptionOutcome => {
  const outcome: ExceptionOutcome = {
    cause,
    operation: policy.operation,
    disposition: EXCEPTION_DISPOSITION.FAILED,
  }
  if (isCancellation(cause)) {
    outcome.disposition = EXCEPTION_DISPOSITION.CANCELLED
    return outcome
  }

  switch (policy.disposition) {
    case EXCEPTION_DISPOSITION.EXPECTED:
      if (policy.isExpectedError(cause)) {
        outcome.disposition = EXCEPTION_DISPOSITION.EXPECTED
        return outcome
      }
      break
    case EXCEPTION_DISPOSITION.RECOVERED:
      outcome.disposition = EXCEPTION_DISPOSITION.RECOVERED
      reporter.info(policy.message ?? 'Operation recovered.', {
        ...policy.context,
        operation: policy.operation,
        reason: policy.reason,
      })
      return outcome
    case EXCEPTION_DISPOSITION.RETRYING:
      if (
        Number.isInteger(policy.attempt) &&
        Number.isInteger(policy.maxAttempts) &&
        policy.attempt > 0 &&
        policy.attempt < policy.maxAttempts
      ) {
        outcome.disposition = EXCEPTION_DISPOSITION.RETRYING
        reporter.info(policy.message ?? 'Operation will retry.', {
          ...policy.context,
          operation: policy.operation,
          reason: policy.reason,
          attempt: policy.attempt,
          max_attempts: policy.maxAttempts,
        })
        return outcome
      }
      break
    case EXCEPTION_DISPOSITION.DELEGATED:
      if (policy.owner.trim()) {
        outcome.disposition = EXCEPTION_DISPOSITION.DELEGATED
        return outcome
      }
      break
  }

  reporter.error(policy.message ?? 'Operation failed.', {
    error: cause,
    operation: policy.operation,
    ...(policy.context ? { context: policy.context } : {}),
  })
  return outcome
}
